import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Scissors, Palette, Ruler, Layers, Stethoscope, Crown as CrownIcon,
  Sparkles, ListChecks, Sticker,
} from "lucide-react";

const HAIR_TYPES = [
  { v: "lisci", l: "Lisci" },
  { v: "mossi", l: "Mossi" },
  { v: "ricci", l: "Ricci" },
  { v: "afro",  l: "Afro" },
];
const COLORS = [
  { v: "biondo_chiaro",   l: "Biondo chiaro" },
  { v: "biondo",          l: "Biondo" },
  { v: "castano_chiaro",  l: "Castano chiaro" },
  { v: "castano",         l: "Castano" },
  { v: "castano_scuro",   l: "Castano scuro" },
  { v: "nero",            l: "Nero" },
  { v: "rosso",           l: "Rosso" },
];
const LENGTHS = [
  { v: "corto",        l: "Corto" },
  { v: "medio",        l: "Medio" },
  { v: "lungo",        l: "Lungo" },
  { v: "molto_lungo",  l: "Molto lungo" },
];
const TEXTURES = [
  { v: "fine",   l: "Fine" },
  { v: "medio",  l: "Medio" },
  { v: "spesso", l: "Spesso" },
];
const DENSITIES = [
  { v: "bassa", l: "Bassa" },
  { v: "media", l: "Media" },
  { v: "alta",  l: "Alta" },
];
const TREATMENTS = [
  { v: "colore",         l: "Colore" },
  { v: "meches",         l: "Meches" },
  { v: "balayage",       l: "Balayage" },
  { v: "schiariture",    l: "Schiariture" },
  { v: "decolorazione",  l: "Decolorazione" },
  { v: "tonalizzazione", l: "Tonalizzazione" },
];
const CONDITIONS = [
  { v: "sano",          l: "Sano" },
  { v: "secco",         l: "Secco" },
  { v: "danneggiato",   l: "Danneggiato" },
  { v: "sfibrato",      l: "Sfibrato" },
  { v: "trattato",      l: "Trattato chimicamente" },
];
const STYLES = [
  { v: "raccolto",       l: "Raccolto" },
  { v: "semi_raccolto",  l: "Semi raccolto" },
  { v: "sciolto",        l: "Sciolto" },
  { v: "treccia",        l: "Treccia" },
  { v: "chignon",        l: "Chignon" },
  { v: "coda",           l: "Coda" },
];
const HOLDS = [
  { v: "naturale",   l: "Naturale" },
  { v: "media",      l: "Media" },
  { v: "strutturata", l: "Strutturata" },
];
const ACCESSORY_FLAGS = [
  { k: "has_extensions",   l: "Extension" },
  { k: "has_hairpieces",   l: "Posticci" },
  { k: "has_volume_extra", l: "Volume extra" },
  { k: "has_accessories",  l: "Accessori" },
];

function Section({ icon: Icon, title, hint, children, testid }) {
  return (
    <section className="bg-white border border-border rounded-sm" data-testid={testid}>
      <header className="flex items-start gap-3 p-4 md:p-5 border-b border-border">
        <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
        </span>
        <div>
          <h3 className="font-serif-heading text-lg md:text-xl leading-tight">{title}</h3>
          {hint && <p className="text-[11px] md:text-xs text-neutral-500 mt-0.5">{hint}</p>}
        </div>
      </header>
      <div className="p-4 md:p-5">{children}</div>
    </section>
  );
}

function SelectField({ label, value, onChange, options, placeholder = "Seleziona", testid }) {
  return (
    <div>
      <Label className="label-uppercase">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1 rounded-sm h-11" data-testid={testid}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function HairSheet({ client, onPatch }) {
  const h = client.hair || {};
  const treatments = Array.isArray(h.treatments) ? h.treatments : [];

  // Patch a single key on client.hair (immutable)
  const patch = (key, value) => onPatch("hair", { ...h, [key]: value });

  const toggleTreatment = (v) => {
    const set = new Set(treatments);
    if (set.has(v)) set.delete(v); else set.add(v);
    patch("treatments", Array.from(set));
  };

  return (
    <div className="space-y-6" data-testid="hair-sheet">
      {/* CARATTERISTICHE */}
      <Section icon={Scissors} title="Caratteristiche del capello" hint="Tipo, colore naturale, lunghezza, struttura, densità" testid="hair-section-traits">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SelectField label="Tipo capelli" value={h.hair_type} onChange={(v) => patch("hair_type", v)} options={HAIR_TYPES} testid="h-hair-type" />
          <SelectField label="Colore naturale" value={h.color} onChange={(v) => patch("color", v)} options={COLORS} testid="h-color" />
          <SelectField label="Lunghezza" value={h.length} onChange={(v) => patch("length", v)} options={LENGTHS} testid="h-length" />
          <SelectField label="Struttura" value={h.texture} onChange={(v) => patch("texture", v)} options={TEXTURES} testid="h-texture" />
          <SelectField label="Densità" value={h.density} onChange={(v) => patch("density", v)} options={DENSITIES} testid="h-density" />
        </div>
      </Section>

      {/* TRATTAMENTI COLORE */}
      <Section icon={Palette} title="Trattamenti colore" hint="Spunta tutti i trattamenti effettuati" testid="hair-section-treatments">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {TREATMENTS.map((t) => {
            const checked = treatments.includes(t.v);
            return (
              <label
                key={t.v}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border cursor-pointer transition-colors ${
                  checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleTreatment(t.v)}
                  data-testid={`h-treat-${t.v}`}
                />
                <span className="text-sm">{t.l}</span>
              </label>
            );
          })}
        </div>
      </Section>

      {/* STATO + TENUTA */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={Stethoscope} title="Stato del capello" testid="hair-section-condition">
          <SelectField label="Condizione" value={h.condition} onChange={(v) => patch("condition", v)} options={CONDITIONS} testid="h-condition" />
        </Section>

        <Section icon={Layers} title="Tenuta" hint="Stile dell'acconciatura" testid="hair-section-hold">
          <SelectField label="Tenuta" value={h.hold} onChange={(v) => patch("hold", v)} options={HOLDS} testid="h-hold" />
        </Section>
      </div>

      {/* ACCESSORI */}
      <Section icon={Sticker} title="Accessori" hint="Selezioni multiple + note libere" testid="hair-section-accessories">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {ACCESSORY_FLAGS.map((a) => {
            const checked = !!h[a.k];
            return (
              <label
                key={a.k}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm border cursor-pointer transition-colors ${
                  checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => patch(a.k, !!c)}
                  data-testid={`h-acc-${a.k}`}
                />
                <span className="text-sm">{a.l}</span>
              </label>
            );
          })}
        </div>
        <div className="mt-4">
          <Label className="label-uppercase">Note accessori</Label>
          <Textarea
            rows={2}
            value={h.accessories_notes || ""}
            onChange={(e) => patch("accessories_notes", e.target.value)}
            placeholder="Es. extension clip biondo cenere · velo lungo · cerchietto di perle…"
            className="mt-1 rounded-sm"
            data-testid="h-acc-notes"
          />
        </div>
      </Section>

      {/* ACCONCIATURA */}
      <Section icon={CrownIcon} title="Acconciatura" hint="Stile desiderato e descrizione" testid="hair-section-style">
        <div className="grid lg:grid-cols-2 gap-4">
          <SelectField label="Stile" value={h.style} onChange={(v) => patch("style", v)} options={STYLES} testid="h-style" />
          <div>
            <Label className="label-uppercase">Descrizione acconciatura</Label>
            <Textarea
              rows={3}
              value={h.style_description || ""}
              onChange={(e) => patch("style_description", e.target.value)}
              placeholder="Es. raccolto morbido laterale con onde ampie, separazione naturale, accessorio di perle dietro l'orecchio destro…"
              className="mt-1 rounded-sm"
              data-testid="h-style-desc"
            />
          </div>
        </div>
      </Section>

      {/* NOTE HAIR */}
      <Section icon={ListChecks} title="Note hair" hint="Annotazioni e suggerimenti per il giorno dell'evento" testid="hair-section-notes">
        <Textarea
          rows={4}
          value={h.notes || ""}
          onChange={(e) => patch("notes", e.target.value)}
          placeholder="Tempi di asciugatura, prodotti da evitare, allergie, riferimenti visivi…"
          className="rounded-sm"
          data-testid="hair-notes-textarea"
        />
      </Section>
    </div>
  );
}
